import { FLAGS } from '@gozoki/feature-flags'
import type { PermissionTuple } from '~/utils/ability'
import type { PermissionRequirement } from '~/utils/rbac'
import { moduleIndexPermission } from '~/utils/rbacModuleIndex'
import type { GuardedTab } from '~/utils/rbacTabs'

/** Chaque onglet a sa route dédiée sous `/hr/planning/*` (dashboard, calendar, …), un tab = une route. */
export type HrTab = 'dashboard' | 'calendar' | 'collaborators' | 'attendance' | 'absence-calendar'

export const PLANNING_READ: PermissionTuple = ['read', 'hr.planning.weekly-planning']
export const PLANNING_ADD: PermissionTuple = ['add', 'hr.planning.weekly-planning']
export const PLANNING_EDIT: PermissionTuple = ['edit', 'hr.planning.weekly-planning']
export const PLANNING_DELETE: PermissionTuple = ['delete', 'hr.planning.weekly-planning']
export const PLANNING_EXPORT: PermissionTuple = ['export', 'hr.planning.weekly-planning']

export const STAFF_READ: PermissionTuple = ['read', 'hr.staff.collaborator']
export const STAFF_ADD: PermissionTuple = ['add', 'hr.staff.collaborator']
export const STAFF_EXPORT: PermissionTuple = ['export', 'hr.staff.collaborator']
export const STAFF_IMPORT: PermissionTuple = ['import', 'hr.staff.collaborator']

/**
 * Formation d'intégration : ressource dédiée, comme côté backend sur
 * `/hr/collaborators/:id/formation` (`add` / `edit` / `delete`). La fiche elle-même ne porte plus
 * d'`edit` : chaque champ dépend de sa ressource de champs.
 */
export const FORMATION_ADD: PermissionTuple = ['add', 'hr.staff.formation']
export const FORMATION_EDIT: PermissionTuple = ['edit', 'hr.staff.formation']
export const FORMATION_DELETE: PermissionTuple = ['delete', 'hr.staff.formation']

export const CLOCKING_READ: PermissionTuple = ['read', 'hr.clocking.clocking']
export const CLOCKING_EXPORT: PermissionTuple = ['export', 'hr.clocking.clocking']

export const ABSENCE_READ: PermissionTuple = ['read', 'hr.absences.absence']
export const ABSENCE_ADD: PermissionTuple = ['add', 'hr.absences.absence']
export const ABSENCE_DELETE: PermissionTuple = ['delete', 'hr.absences.absence']
export const ABSENCE_EXPORT: PermissionTuple = ['export', 'hr.absences.absence']

/**
 * Les absences d'un collaborateur se saisissent depuis le calendrier des absences comme depuis sa
 * fiche : l'un OU l'autre droit suffit, comme côté backend sur
 * `/hr/collaborators/:id/unavailabilities`.
 */
export const UNAVAILABILITY_ADD: PermissionRequirement = { any: [ABSENCE_ADD, STAFF_ADD] }

/**
 * Onglet « Tableau de bord » (flag `dashboard-rh`) : ressource propre depuis l'audit du 19/09
 * (TAX-M2), en remplacement du OU sur les trois lectures voisines — un rôle qui lisait le planning
 * obtenait sinon l'effectif et les frais de personnel du réseau.
 */
export const HR_DASHBOARD_READ: PermissionTuple = ['read', 'hr.dashboard.overview']

/**
 * Réglages réseau du planning (règles globales, seuils, multiplicateurs de rémunération) : ils
 * valent pour tout le réseau, ils ne peuvent pas dépendre d'un `edit planning` sur une boutique
 * (audit du 19/09, MAJ-06).
 */
export const PLANNING_SETTINGS_READ: PermissionTuple = ['read', 'hr.planning.settings']
export const PLANNING_SETTINGS_EDIT: PermissionTuple = ['edit', 'hr.planning.settings']

/**
 * Publier le planning envoie un SMS à chaque collaborateur : action spéciale, exigée avec l'action
 * de base de la ressource — même exigence que `POST /hr/planning/publish` (TAX-M9).
 */
export const PLANNING_PUBLISH: PermissionRequirement = [
  PLANNING_EDIT,
  ['publish-planning', 'hr.planning.weekly-planning'],
]

/** Accès à l'écran : dès qu'un seul des sous-onglets est accessible. */
export const HR_PAGE_READ: PermissionRequirement = moduleIndexPermission([
  HR_DASHBOARD_READ,
  PLANNING_READ,
  STAFF_READ,
  CLOCKING_READ,
  ABSENCE_READ,
])

/**
 * Source unique des droits de lecture des sous-onglets RH.
 *
 * Consommée par la barre d'onglets, par le `staticData.permission` des routes et par la
 * redirection de `/hr/planning` : un seul endroit à mettre à jour, pas de dérive possible entre
 * l'onglet affiché et son contenu.
 */
export const HR_TAB_PERMISSIONS = {
  dashboard: HR_DASHBOARD_READ,
  calendar: PLANNING_READ,
  collaborators: STAFF_READ,
  attendance: CLOCKING_READ,
  'absence-calendar': ABSENCE_READ,
} as const satisfies Record<HrTab, PermissionRequirement>

/** Chemin de la route dédiée de chaque onglet — source unique consommée par la barre d'onglets
 * (navigation) et par la redirection de `/hr/planning`. */
export const HR_TAB_ROUTE = {
  dashboard: '/hr/planning/dashboard',
  calendar: '/hr/planning/calendar',
  collaborators: '/hr/planning/collaborators',
  attendance: '/hr/planning/attendance',
  'absence-calendar': '/hr/planning/absence-calendar',
} as const satisfies Record<HrTab, string>

/** Les onglets n'ayant pas de route dédiée, chacun porte explicitement son exigence. */
type HrTabDefinition = GuardedTab<HrTab> & { permission: PermissionRequirement }

/** Ordre de déclaration = ordre d'affichage, et ordre de repli quand l'onglet actif est interdit. */
export const HR_TABS: HrTabDefinition[] = [
  {
    value: 'dashboard',
    label: 'Tableau de bord',
    permission: HR_TAB_PERMISSIONS.dashboard,
    // Masqué par la barre RH tant que le flag est éteint, et absent de la palette ⌘K.
    flagKey: FLAGS.dashboardRh,
  },
  { value: 'calendar', label: 'Planning', permission: HR_TAB_PERMISSIONS.calendar },
  { value: 'collaborators', label: 'Personnel', permission: HR_TAB_PERMISSIONS.collaborators },
  { value: 'attendance', label: 'Pointage', permission: HR_TAB_PERMISSIONS.attendance },
  {
    value: 'absence-calendar',
    label: 'Calendrier Absences',
    permission: HR_TAB_PERMISSIONS['absence-calendar'],
  },
]
